/*
 * The parcel's trips.
 *
 * One number per order was wrong the first time a parcel came back: a wrong
 * item goes out, the customer sends it back, the right one goes out — three
 * trips, three tracking numbers, and staff need all of them to answer "where
 * is it?" and "which trip was that?". So an order carries a list of
 * `shipments` (admin/orders/shipments), each one trip in one direction, the
 * way Shopify lists an order's fulfillments. The backend mirrors the newest
 * trip to the customer onto `tracking_number`, which is what the storefront
 * and the apps show.
 */

import { ORDER_STATUS } from './orderConstants';

export const SHIPMENT_DIRECTION = {
  OUTBOUND: 'outbound',
  RETURN: 'return',
};

// Tamex is the only courier; fulfilment is manual on its portal.
export const COURIER_LABEL = 'Tamex';

// Plain words for the people on this screen: "outbound" is courier jargon.
const DIRECTION_LABEL = {
  [SHIPMENT_DIRECTION.OUTBOUND]: 'To customer',
  [SHIPMENT_DIRECTION.RETURN]: 'Return',
};

export const directionLabel = (direction) =>
  DIRECTION_LABEL[direction] || String(direction || '');

export const isReturnTrip = (shipment) =>
  shipment?.direction === SHIPMENT_DIRECTION.RETURN;

/** Newest first. The API already answers that way; this keeps it true locally. */
export const sortNewestFirst = (shipments) =>
  [...(Array.isArray(shipments) ? shipments : [])].sort(
    (a, b) => (Number(b?.id) || 0) - (Number(a?.id) || 0),
  );

/** The newest trip in a direction, or null — the outbound one is what the customer tracks. */
export const newestTrip = (shipments, direction) =>
  sortNewestFirst(shipments).find((shipment) => shipment?.direction === direction) || null;

/** The trip the parcel is on now: the newest row, whichever way it went. */
export const currentTrip = (shipments) => sortNewestFirst(shipments)[0] || null;

/**
 * Which way a parcel travels when an order enters a status, or null for the
 * statuses that move no parcel. "Handed to shipping company" starts a trip
 * out; "Returned" starts a trip back.
 */
export const tripDirectionForStatus = (statusId) => {
  if (statusId === ORDER_STATUS.HANDED_TO_SHIPPING) return SHIPMENT_DIRECTION.OUTBOUND;
  if (statusId === ORDER_STATUS.RETURNED) return SHIPMENT_DIRECTION.RETURN;
  return null;
};

/**
 * The number already on file for a move that starts a trip in `direction`,
 * or null when the move needs a new one. Only the newest trip counts: a
 * parcel that went out, came back and goes out again needs a fresh number,
 * however many old ones the order holds. A number keyed in ahead of the
 * move, or a correction back and forth, is found and not asked for twice.
 */
export const tripNumberOnFile = (shipments, direction) => {
  const trip = currentTrip(shipments);
  return trip && trip.direction === direction ? trip.tracking_number : null;
};
